import React from "react";
import api from "../../lib/apiClient";
import { toast } from "sonner";
import { ScrollArea } from "../ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Bookmark, Clock, MessageCircle, Repeat, RefreshCw, Star, Trash2, Video, X } from "lucide-react";
import XSelfShareButton from "./XSelfShareButton";
import { cacheInvalidate } from "../../lib/cache";
// API import removed — apiClient handles baseURL now.

// Compute the days remaining until expires_at. Returns null when the
// video doesn't carry an expires_at (legacy rows).
const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

/**
 * Owner-side videos list + selected-video details panel with quick highlight
 * toggle, delete, and comment moderation. Parent owns all data + handlers —
 * this is just the rendering shell.
 */
const MyVideosTab = ({
  videos,
  selectedVideo,
  setSelectedVideo,
  videoComments,
  loadingComments,
  onToggleHighlight,
  onDeleteVideo,
  onDeleteComment,
  onRenewVideo,
  isProducer,
  selfShareUsedToday,
  selfShareRemaining,
  onSelfShared,
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Videos List */}
    <div className="lg:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">My Videos ({videos.length})</h3>
        <p className="text-gray-400 text-sm">
          <Star className="h-4 w-4 inline text-amber-400 mr-1" />
          {videos.filter(v => v.highlighted).length}/4 highlighted
        </p>
      </div>
      {videos.length > 0 ? (
        <div className="space-y-3">
          {videos.map((video) => (
            <div
              key={video.id}
              className={`bg-gray-900 border rounded-lg p-3 cursor-pointer transition-all ${
                selectedVideo?.id === video.id
                  ? 'border-emerald-500 ring-1 ring-emerald-500/50'
                  : 'border-gray-800 hover:border-gray-700'
              }`}
              onClick={() => setSelectedVideo(video)}
            >
              <div className="flex items-start gap-3">
                <div className="relative w-24 h-16 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                  <video src={video.video_url} className="w-full h-full object-cover" muted />
                  {video.highlighted && (
                    <div className="absolute top-1 right-1 bg-amber-500 rounded p-0.5">
                      <Star className="h-3 w-3 text-white" fill="white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-sm truncate">{video.title}</h4>
                  {(video.location?.city || video.location?.country) && (
                    <p className="text-gray-400 text-xs">
                      {[video.location?.city, video.location?.country].filter(Boolean).join(", ")}
                    </p>
                  )}
                  <p className="text-gray-500 text-xs mt-1 flex items-center gap-2 flex-wrap" data-testid={`video-card-stats-${video.id}`}>
                    <span>{new Date(video.created_at).toLocaleDateString()}</span>
                    <span className="text-gray-600">·</span>
                    <span>{video.applaud_count} applauds</span>
                    {/* Saved-to-Trail badge — only renders when at
                        least one viewer has saved the video. This
                        avoids demotivating new producers with a row
                        of "0 saves" indicators. */}
                    {(video.trail_count || 0) > 0 && (
                      <>
                        <span className="text-gray-600">·</span>
                        <span
                          className="inline-flex items-center gap-0.5 text-amber-300"
                          data-testid={`video-card-trail-badge-${video.id}`}
                          title={`Saved by ${video.trail_count} viewer${video.trail_count === 1 ? '' : 's'}`}
                        >
                          <Bookmark className="h-3 w-3 fill-amber-300" />
                          {video.trail_count} saved
                        </span>
                      </>
                    )}
                    {/* Expiry badge — amber inside 3 days, red when
                        already past expiry. Hidden when expires_at is
                        absent (legacy rows). */}
                    {(() => {
                      const d = daysUntil(video.expires_at);
                      if (d === null) return null;
                      const expired = d <= 0;
                      const soon = d <= 3 && d > 0;
                      const cls = expired
                        ? 'text-red-300'
                        : soon ? 'text-amber-300' : 'text-gray-500';
                      const label = expired
                        ? 'Expired'
                        : d === 1 ? 'Expires in 1 day' : `Expires in ${d} days`;
                      return (
                        <>
                          <span className="text-gray-600">·</span>
                          <span
                            className={`inline-flex items-center gap-0.5 ${cls}`}
                            data-testid={`video-card-expiry-${video.id}`}
                            title={`Expires ${new Date(video.expires_at).toLocaleString()}`}
                          >
                            <Clock className="h-3 w-3" />
                            {label}
                          </span>
                        </>
                      );
                    })()}
                    {video.auto_renew && (
                      <>
                        <span className="text-gray-600">·</span>
                        <span
                          className="inline-flex items-center gap-0.5 text-cyan-300"
                          data-testid={`video-card-auto-renew-${video.id}`}
                          title="Auto-renew is on — this video extends itself"
                        >
                          <Repeat className="h-3 w-3" />
                          Auto
                        </span>
                      </>
                    )}
                  </p>
                </div>
                <div className="flex gap-1">
                  {/* Renew button — only surfaced when the row exposes
                      expires_at and onRenewVideo was provided. Hidden
                      otherwise to avoid a dead button on legacy rows. */}
                  {onRenewVideo && video.expires_at && (() => {
                    const d = daysUntil(video.expires_at);
                    const urgent = d !== null && d <= 3;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRenewVideo(video); }}
                        data-testid={`video-card-renew-${video.id}`}
                        className={`p-1.5 rounded transition-colors ${
                          urgent
                            ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                            : 'bg-gray-800 text-gray-400 hover:text-emerald-300'
                        }`}
                        title="Renew this video"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    );
                  })()}
                  <XSelfShareButton
                    videoId={video.id}
                    isProducer={isProducer}
                    usedToday={selfShareUsedToday}
                    remainingToday={selfShareRemaining}
                    onShared={onSelfShared}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleHighlight(video); }}
                    className={`p-1.5 rounded transition-colors ${
                      video.highlighted
                        ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                        : 'bg-gray-800 text-gray-400 hover:text-amber-400'
                    }`}
                    title={video.highlighted ? "Remove from featured" : "Add to featured"}
                  >
                    <Star className="h-4 w-4" fill={video.highlighted ? "currentColor" : "none"} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteVideo(video); }}
                    className="p-1.5 rounded bg-gray-800 text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete video"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12 text-gray-400 bg-gray-900 rounded-lg border border-gray-800">
          <Video className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm sm:text-base">No videos yet. Create your first one!</p>
        </div>
      )}
    </div>

    {/* Video Details & Comments Panel */}
    <div className="lg:col-span-1">
      {selectedVideo ? (
        <Card className="bg-gray-900 border-gray-800 sticky top-20">
          <CardHeader className="p-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-base">Video Details</CardTitle>
              <button onClick={() => setSelectedVideo(null)} className="text-gray-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            <video src={selectedVideo.video_url} className="w-full aspect-video rounded bg-black" controls />
            <div>
              <h4 className="text-white font-semibold">{selectedVideo.title}</h4>
              {(selectedVideo.location?.city || selectedVideo.location?.country) && (
                <p className="text-gray-400 text-sm">
                  {[selectedVideo.location?.city, selectedVideo.location?.country].filter(Boolean).join(", ")}
                </p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                {new Date(selectedVideo.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => onToggleHighlight(selectedVideo)}
                size="sm"
                className={selectedVideo.highlighted
                  ? "bg-amber-600 hover:bg-amber-700 flex-1"
                  : "bg-gray-800 hover:bg-gray-700 flex-1"}
              >
                <Star className="h-4 w-4 mr-1" fill={selectedVideo.highlighted ? "white" : "none"} />
                {selectedVideo.highlighted ? "Featured" : "Add to Featured"}
              </Button>
              <Button
                onClick={() => onDeleteVideo(selectedVideo)}
                size="sm"
                variant="destructive"
                className="bg-red-600/20 hover:bg-red-600/30 text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Owner-display toggle — appears only on videos this user
                ACQUIRED (i.e. owner != original producer). Cosmetic
                switch: show the producer's credit + affiliate links
                (50/50 applause split) or hide them (100% to owner). */}
            {selectedVideo.sold_at &&
             selectedVideo.producer_username &&
             selectedVideo.owner_username &&
             selectedVideo.producer_username !== selectedVideo.owner_username && (
              <div className="bg-gray-800/60 border border-amber-500/30 rounded-lg p-3" data-testid="owner-display-toggle">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-medium">
                      {selectedVideo.show_original_producer === false
                        ? "Showing as Owned by you"
                        : `Crediting @${selectedVideo.producer_username}`}
                    </p>
                    <p className="text-gray-500 text-[10px] leading-tight mt-0.5">
                      {selectedVideo.show_original_producer === false
                        ? "100% of applause credits come to you. Your affiliate links display."
                        : "Half of applause credits go to the original producer. Their affiliate links display."}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const next = !(selectedVideo.show_original_producer !== false);
                        await api.patch(
                          `/videos/${selectedVideo.id}/owner-display`,
                          { show_original_producer: next },
                        );
                        selectedVideo.show_original_producer = next;
                        // Bust the public-profile cache for both the
                        // current owner AND the original producer, so
                        // affiliate-link strips and "produced by" credit
                        // both refresh the moment the toggle flips.
                        try {
                          const ownerHandle = selectedVideo.owner_username || selectedVideo.username;
                          if (ownerHandle) cacheInvalidate(`profile:${ownerHandle.toLowerCase()}`);
                          if (selectedVideo.producer_username) {
                            cacheInvalidate(`profile:${selectedVideo.producer_username.toLowerCase()}`);
                          }
                        } catch (_e) { /* noop */ }
                        toast.success(next ? "Producer credited." : "Now shown as Owned by you.");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Couldn't toggle");
                      }
                    }}
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 shrink-0"
                    data-testid="owner-display-toggle-btn"
                  >
                    Flip
                  </Button>
                </div>
              </div>
            )}
            <div>
              <h5 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-cyan-400" />
                Comments ({videoComments.length})
              </h5>
              <ScrollArea className="h-48 bg-gray-800 rounded-lg p-2">
                {loadingComments ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full" />
                  </div>
                ) : videoComments.length > 0 ? (
                  <div className="space-y-2">
                    {videoComments.map((comment) => (
                      <div key={comment.id} className="bg-gray-900 rounded p-2 group">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-emerald-400 text-xs font-medium">@{comment.username}</p>
                            <p className="text-gray-300 text-sm mt-0.5">{comment.content}</p>
                            <p className="text-gray-600 text-xs mt-1">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => onDeleteComment(comment.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-400 transition-all"
                            title="Delete comment"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No comments yet</p>
                )}
              </ScrollArea>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-8 text-center text-gray-500">
            <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Select a video to view details and manage comments</p>
          </CardContent>
        </Card>
      )}
    </div>
  </div>
);

export default MyVideosTab;
